import React, { useEffect } from 'react';
import type { HomeProps } from '../../../shells/types';

/**
 * Phase 73 full — Teens B · Command Deck Home surface.
 *
 * Linear/Notion power-user shell. ⌘K (or Ctrl+K) bridges to the
 * Phase 73 interim sass-surface-map drawer which serves as the
 * command palette (grouped, searchable, one-tap to navigate). Stat
 * cards offer one-tap entry to the four most common power-user
 * destinations.
 *
 * Reskin only — every action bridges to an existing surface.
 */
export function DeckHome({ ctx }: HomeProps): JSX.Element {
  const go = (target: string, reason: string) => {
    ctx.director.bridge(target, reason);
  };

  // ⌘K / Ctrl+K opens the surface-map palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        go('sass-surface-map', 'deck-cmd-k');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="deck-home" data-testid="deck-home">
      <button
        type="button"
        className="deck-cmd"
        data-testid="deck-cmd-palette"
        onClick={() => go('sass-surface-map', 'deck-cmd-click')}
      >
        <span className="deck-cmd-glyph" aria-hidden>🔎</span>
        <span className="deck-cmd-text">Search · run a command — jump to any surface</span>
        <span className="deck-cmd-kbd">⌘K</span>
      </button>

      <div className="deck-stats">
        <button type="button" className="deck-stat" data-testid="deck-stat-unread" onClick={() => go('sass-chats', 'deck-stat-unread')}>
          <span className="deck-stat-label">Unread</span>
          <span className="deck-stat-value deck-stat-accent-pink">5</span>
          <span className="deck-stat-sub">across 3 chats</span>
        </button>
        <button type="button" className="deck-stat" data-testid="deck-stat-jobs" onClick={() => go('env-board', 'deck-stat-jobs')}>
          <span className="deck-stat-label">Open jobs</span>
          <span className="deck-stat-value deck-stat-accent-amber">3</span>
          <span className="deck-stat-sub">within 0.5 mi</span>
        </button>
        <button type="button" className="deck-stat" data-testid="deck-stat-friends" onClick={() => go('contact-picker', 'deck-stat-friends')}>
          <span className="deck-stat-label">Friends</span>
          <span className="deck-stat-value deck-stat-accent-blue">7<span style={{ fontSize: 16, color: 'var(--ink-dim)' }}>/14</span></span>
          <span className="deck-stat-sub">2 in a call</span>
        </button>
        <button type="button" className="deck-stat" data-testid="deck-stat-votes" onClick={() => go('sass-prepublish-vote', 'deck-stat-votes')}>
          <span className="deck-stat-label">Votes</span>
          <span className="deck-stat-value deck-stat-accent-red">1</span>
          <span className="deck-stat-sub">2/3 to publish</span>
        </button>
      </div>

      <div className="deck-panel" data-testid="deck-panel-inbox">
        <div className="deck-panel-title">
          Inbox
          <button type="button" className="deck-panel-link" data-testid="deck-panel-inbox-link" onClick={() => go('sass-chats', 'deck-inbox-all')}>open all →</button>
        </div>
        <div role="button" tabIndex={0} className="deck-row" onClick={() => go('sass-chats', 'deck-row-dm')} onKeyDown={(e) => { if (e.key === 'Enter') go('sass-chats', 'deck-row-dm'); }}>
          <span className="deck-row-glyph" aria-hidden>💬</span>
          <span className="deck-row-title">Direct messages</span>
          <span className="deck-row-sub">2 unread</span>
        </div>
        <div role="button" tabIndex={0} className="deck-row" onClick={() => go('social-channel', 'deck-row-channel')} onKeyDown={(e) => { if (e.key === 'Enter') go('social-channel', 'deck-row-channel'); }}>
          <span className="deck-row-glyph" aria-hidden>📡</span>
          <span className="deck-row-title">#neighborhood</span>
          <span className="deck-row-sub">12 new</span>
        </div>
        <div role="button" tabIndex={0} className="deck-row" onClick={() => go('news-paper', 'deck-row-news')} onKeyDown={(e) => { if (e.key === 'Enter') go('news-paper', 'deck-row-news'); }}>
          <span className="deck-row-glyph" aria-hidden>📰</span>
          <span className="deck-row-title">News</span>
          <span className="deck-row-sub">200 headlines · BBC + NPR</span>
        </div>
      </div>

      <div className="deck-panel" data-testid="deck-panel-help">
        <div className="deck-panel-title">
          Help nearby
          <button type="button" className="deck-panel-link" data-testid="deck-panel-help-link" onClick={() => go('env-board', 'deck-help-board')}>board →</button>
        </div>
        <div role="button" tabIndex={0} className="deck-row" onClick={() => go('env-board', 'deck-row-help-1')} onKeyDown={(e) => { if (e.key === 'Enter') go('env-board', 'deck-row-help-1'); }}>
          <span className="deck-row-glyph" aria-hidden>🐕</span>
          <span className="deck-row-title">Lost dog at maple + 4th</span>
          <span className="deck-row-sub">12m</span>
        </div>
        <div role="button" tabIndex={0} className="deck-row" onClick={() => go('env-board', 'deck-row-help-2')} onKeyDown={(e) => { if (e.key === 'Enter') go('env-board', 'deck-row-help-2'); }}>
          <span className="deck-row-glyph" aria-hidden>🪴</span>
          <span className="deck-row-title">Plants need watering (apt 4B)</span>
          <span className="deck-row-sub">1h</span>
        </div>
      </div>
    </div>
  );
}
